import { SyntheticEvent, useState } from "react";
import { useTranslator } from "@eo-locale/react";

import {
  Box,
  Button,
  Alert,
  CloseButton,
  Field,
  HStack,
  PinInput,
  HTMLWrapper
} from "@/storybook-components";
import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import { saveUserConfirmation } from "@/redux/features/user-confirmation/thunk";
import { resetOtpError } from "@/redux/features/user-confirmation/slice";

const OTP_LENGTH = 6;

const VerificationForm = (): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const [otp, setOtp] = useState<string>("");
  const { registrations } = useAppSelector((state) => state.registrations);
  const { status, error } = useAppSelector((state) => state.userConfirmation);

  const handleSubmit = async (e: SyntheticEvent): Promise<void> => {
    e.preventDefault();
    dispatch(saveUserConfirmation(otp));
  };

  return (
    <Box as="form" my={2} noValidate onSubmit={handleSubmit}>
      <HTMLWrapper
        data-testid="accountVerification-yourEmailText"
        html={translate("register.verifyCodeHelp", {
          length: OTP_LENGTH,
          email: registrations?.email || translate("register.yourEmail")
        })}
      />
      <Field.Root
        my={4}
        required
        id="otp"
        disabled={status === "saving"}
        invalid={error?.length > 0}
      >
        <Field.Label textAlign="center" fontWeight="semibold">
          {translate("register.enterVerificationCode")}
        </Field.Label>
        <HStack justifyContent="center">
          <PinInput
            otp
            autoFocus
            count={OTP_LENGTH}
            onChange={setOtp}
            onComplete={setOtp}
            disabled={status === "saving"}
            invalid={error?.length > 0}
            getInputProps={(index) => ({
              "data-testid": `accountVerification-pinInput-${index + 1}`
            })}
          />
        </HStack>
        {error && (
          <Alert.Root status="error" borderRadius="md" my={5}>
            <Alert.Indicator />
            {translate("register.pinError")}
            <CloseButton
              ml="auto"
              color="white"
              _hover={{ bg: "whiteAlpha.300" }}
              _active={{ bg: "whiteAlpha.400" }}
              onClick={() => dispatch(resetOtpError())}
            />
          </Alert.Root>
        )}
      </Field.Root>

      <Button
        variant="primary"
        type="submit"
        width="full"
        loading={status === "saving"}
        disabled={otp.length !== OTP_LENGTH}
        data-testid="accountVerification-confirmAccountButton"
      >
        {translate("register.confirmAccount")}
      </Button>
    </Box>
  );
};

export default VerificationForm;
